/**
 * Initiative Store — VIEW over encounterStore
 *
 * Previously held its own state (entries, round, currentIndex, isInCombat) and
 * was one of three stores tracking initiative. Now derives everything from
 * encounterStore.activeEncounter via a subscription mirror; all write methods
 * forward to encounterStore (the canonical DB-backed source).
 *
 * Component APIs are preserved — `useInitiativeStore(s => s.entries)` still
 * works. But there is now ONE writer: encounterStore. Persistence is
 * guaranteed; drift between stores is impossible by construction.
 */

import { create } from 'zustand';
import { useEncounterStore, type Encounter, type Participant } from './encounterStore';

export interface InitiativeEntry {
  id: string;
  name: string;
  initiative: number;
  dexMod: number;
  type: 'character' | 'mob';
  characterId?: string;
  badgeId?: string;
  badgeIcon?: string;
  mobType?: string;
  count?: number;
  isActive: boolean;
  hasActed: boolean;
  conditions: string[];
}

interface InitiativeState {
  // Derived state (mirrored from encounterStore)
  entries: InitiativeEntry[];
  currentIndex: number;
  round: number;
  isInCombat: boolean;

  // Command forwarders — route writes to encounterStore
  startCombat: () => void;
  endCombat: () => void;
  nextTurn: () => void;
  previousTurn: () => void;
  setCurrentIndex: (index: number) => void;

  addEntry: (entry: Omit<InitiativeEntry, 'isActive' | 'hasActed'>) => void;
  removeEntry: (id: string) => void;
  updateEntry: (id: string, updates: Partial<InitiativeEntry>) => void;
  setInitiative: (id: string, initiative: number) => void;

  rollAllInitiative: () => void;
  resetInitiative: () => void;
  clearAllEntries: () => void;
  sortByInitiative: () => void;

  getCurrentEntry: () => InitiativeEntry | null;
  getNextEntry: () => InitiativeEntry | null;
}

// =============================================================================
// DERIVATION — encounter → initiative entries
// =============================================================================

function sortParticipants(ps: Participant[]): Participant[] {
  return [...ps].sort((a, b) => {
    if (b.initiative !== a.initiative) return b.initiative - a.initiative;
    return b.dexMod - a.dexMod;
  });
}

// EncounterStore stores raw UUIDs as participant.id. The InitiativePanel UI
// historically uses `char-${uuid}` as entry.id for characters (mobs use raw
// UUID). We translate at the boundary.
function toPanelId(p: Participant): string {
  return p.type === 'character' ? `char-${p.id}` : p.id;
}

function stripCharPrefix(id: string): string {
  return id.startsWith('char-') ? id.slice(5) : id;
}

function deriveState(enc: Encounter | null): {
  entries: InitiativeEntry[];
  currentIndex: number;
  round: number;
  isInCombat: boolean;
} {
  if (!enc) {
    return { entries: [], currentIndex: -1, round: 0, isInCombat: false };
  }

  const sorted = sortParticipants(Array.from(enc.participants.values()));
  const currentIndex = enc.currentTurnId
    ? sorted.findIndex((p) => p.id === enc.currentTurnId)
    : -1;

  const entries: InitiativeEntry[] = sorted.map((p, i) => ({
    id: toPanelId(p),
    name: p.name,
    initiative: p.initiative,
    dexMod: p.dexMod,
    type: p.type,
    characterId: p.type === 'character' ? p.id : undefined,
    isActive: i === currentIndex,
    hasActed: p.hasActed,
    conditions: [],
  }));

  return {
    entries,
    currentIndex,
    round: enc.round,
    isInCombat: enc.status === 'active',
  };
}

// =============================================================================
// STORE
// =============================================================================

export const useInitiativeStore = create<InitiativeState>()((_set, get) => {
  // Attempt to read initial state (encounterStore may not be initialized yet)
  let initial: ReturnType<typeof deriveState>;
  try {
    initial = deriveState(useEncounterStore.getState().activeEncounter);
  } catch {
    initial = { entries: [], currentIndex: -1, round: 0, isInCombat: false };
  }

  return {
    entries: initial.entries,
    currentIndex: initial.currentIndex,
    round: initial.round,
    isInCombat: initial.isInCombat,

    // =========================================================================
    // COMMANDS — all writes delegate to encounterStore
    // =========================================================================

    startCombat: () => {
      const encStore = useEncounterStore.getState();
      if (!encStore.activeEncounter) return;
      encStore.beginCombat();
    },

    endCombat: () => {
      useEncounterStore.getState().endEncounter();
    },

    nextTurn: () => {
      useEncounterStore.getState().nextTurn();
    },

    previousTurn: () => {
      useEncounterStore.getState().prevTurn();
    },

    setCurrentIndex: (index) => {
      const enc = useEncounterStore.getState().activeEncounter;
      if (!enc) return;
      const sorted = sortParticipants(Array.from(enc.participants.values()));
      const target = sorted[index];
      if (!target) return;
      useEncounterStore.setState({
        activeEncounter: { ...enc, currentTurnId: target.id },
      });
      useEncounterStore.getState().syncToSupabase();
    },

    addEntry: (entry) => {
      const encStore = useEncounterStore.getState();
      if (!encStore.activeEncounter) {
        console.warn('[initiativeStore] addEntry called with no active encounter — ignored');
        return;
      }
      // Resolve raw UUID — callers may pass `char-${uuid}` or a raw UUID.
      // Prefer explicit characterId when present.
      const rawId = entry.type === 'character'
        ? (entry.characterId ?? stripCharPrefix(entry.id))
        : entry.id;
      encStore.addParticipant(
        rawId,
        entry.type,
        entry.name,
        entry.initiative,
        entry.dexMod,
      );
    },

    removeEntry: (id) => {
      useEncounterStore.getState().removeParticipant(stripCharPrefix(id));
    },

    updateEntry: (id, updates) => {
      const rawId = stripCharPrefix(id);
      if (typeof updates.initiative === 'number') {
        useEncounterStore.getState().setParticipantInitiative(rawId, updates.initiative);
      }
      if (typeof updates.name === 'string') {
        useEncounterStore.getState().updateParticipantName(rawId, updates.name);
      }
    },

    setInitiative: (id, initiative) => {
      useEncounterStore.getState().setParticipantInitiative(stripCharPrefix(id), initiative);
    },

    rollAllInitiative: () => {
      const enc = useEncounterStore.getState().activeEncounter;
      if (!enc) return;
      for (const p of enc.participants.values()) {
        const newInit = Math.floor(Math.random() * 20) + 1 + p.dexMod;
        useEncounterStore.getState().setParticipantInitiative(p.id, newInit);
      }
    },

    resetInitiative: () => {
      const enc = useEncounterStore.getState().activeEncounter;
      if (!enc) return;
      for (const p of enc.participants.values()) {
        useEncounterStore.getState().setParticipantInitiative(p.id, 0);
      }
    },

    clearAllEntries: () => {
      useEncounterStore.getState().endEncounter();
    },

    sortByInitiative: () => {
      // No-op — entries are always sorted on read via deriveState.
    },

    // =========================================================================
    // QUERIES
    // =========================================================================

    getCurrentEntry: () => {
      const { entries, currentIndex } = get();
      return currentIndex >= 0 && currentIndex < entries.length
        ? entries[currentIndex]
        : null;
    },

    getNextEntry: () => {
      const { entries, currentIndex } = get();
      if (entries.length === 0) return null;
      const nextIndex = (currentIndex + 1) % entries.length;
      return entries[nextIndex];
    },
  };
});

// =============================================================================
// MIRROR — encounterStore → initiativeStore
// =============================================================================
//
// Subscribes once at module load. Every change to the encounter re-derives
// the initiative view and pushes it into the store. Components that read
// `useInitiativeStore(s => s.entries)` see updates automatically.
//
// This replaces the old `pushToInitiativeStore()` function in encounterStore
// which had to be called manually from every mutation. Now it's automatic.
// =============================================================================

useEncounterStore.subscribe((state) => {
  const derived = deriveState(state.activeEncounter);
  useInitiativeStore.setState(derived);
});
